/**
 * useDiscoverSchema — POST /enterprise/connectors/{conn_id}/discover.
 *
 * Kicks off the BE background profiling task. The BE responds 202
 * immediately with ``{status: "started"}`` — actual progress lives in
 * ``useProfilingStatus`` (which the caller mounts in parallel and
 * polls).
 *
 * ``onSuccess`` invalidates ``enterpriseProfilingKey`` so a fresh poll
 * fires the moment discovery starts (no waiting for the next 2s tick).
 * Schema cache is invalidated only on terminal status by the caller —
 * polling-based invalidation here would thrash the cache.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { type DiscoverResponse, discoverEnterpriseSchema } from "@ando/core/api";

import { enterpriseProfilingKey } from "./enterpriseKeys";

export interface UseDiscoverSchemaVars {
  readonly connId: string;
}

export function useDiscoverSchema() {
  const qc = useQueryClient();
  return useMutation<DiscoverResponse, Error, UseDiscoverSchemaVars>({
    mutationFn: ({ connId }) => discoverEnterpriseSchema(connId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: enterpriseProfilingKey(vars.connId),
      });
    },
  });
}
